// ============================================================================
// contexts/liveStreamManagment/SourceRegistryContext.tsx
// Provider React mince pour le SourceRegistry.
//
// Responsabilités :
//   - Créer et posséder l'instance unique du registry
//   - Exposer le registry + les acquirers aux composants
//   - Forcer un re-render quand l'état des sources change
//   - Détruire le registry au démontage
//
// Ce contexte ne contient PAS de logique métier — c'est un pont entre
// la classe pure SourceRegistry et l'arbre React.
//
// API publique :
//   - borrow() : UNIQUE point d'entrée pour obtenir un stream
//   - release() : libérer un emprunt (mais préférer handle.release() du borrow)
//   - register/unregister : gestion du cycle de vie des sources
//   - injectStream/ejectStream : pour les streams externes (remote, scene)
//
// acquire() n'est PAS exposée : c'est une méthode interne du registry.
// ============================================================================

import React, {
  createContext, useContext, useEffect, useRef, useState,
  useCallback, useMemo,
  ReactNode,
} from 'react';

import {
  SourceRegistry,
  SourceRegistryAcquirers,
  createSourceRegistry,
  Source,
  SourceType,
  SourceState,
  SourceEvent,
  BorrowHandle,
  RegisterOptions,
  PERSISTENT_SOURCE_TYPES,
} from '../../dataTypes/liveStreamManagment/sourceRegistry';

import { useMediaStream, DEFAULT_SCREEN_SHARE_SLOTS } from '../MediaStreamContext';

// ─── Interface du contexte ────────────────────────────────────────────────────

interface SourceRegistryContextType {
  /** L'instance du registry (pour les cas avancés / hooks custom) */
  registry:  SourceRegistry;
  /** Les acquirers individuels (méthodes spécifiques : preload, getVideoElement…) */
  acquirers: SourceRegistryAcquirers;

  // ── Raccourcis pour les opérations courantes ─────────────────────────────

  /** Enregistre une source */
  register:     (options: RegisterOptions) => Source;
  /** Retire une source */
  unregister:   (sourceId: string) => void;
  /** Libère un emprunt (décrémente refCount) */
  release:      (sourceId: string) => void;
  /** Libère immédiatement quel que soit le refCount */
  forceRelease: (sourceId: string) => void;
  /** Emprunte un flux — UNIQUE point d'entrée pour obtenir un stream */
  borrow:       (sourceId: string) => Promise<BorrowHandle>;
  /** Injecte un stream externe (remote, scene) */
  injectStream: (sourceId: string, stream: MediaStream) => void;
  /** Retire un stream externe */
  ejectStream:  (sourceId: string) => void;
  /**
   * Libère tout ce qui appartenait à la chaîne qu'on quitte, en épargnant les
   * périphériques locaux (`device`, `screen`). Cf. `releaseChannelScopedSources`.
   */
  releaseChannelScopedSources: () => void;

  // ── Lecture de l'état (réactif — déclenche un re-render) ─────────────────

  /** Toutes les sources enregistrées (snapshot réactif) */
  sources:      Source[];
  /** Toutes les sources d'un type donné */
  sourcesByType: (type: SourceType) => Source[];
  /** Une source par id */
  getSource:    (id: string) => Source | undefined;
  /** Le stream d'une source */
  getStream:    (id: string) => MediaStream | null;
  /** L'état d'une source */
  getState:     (id: string) => SourceState;
}

// ─── Contexte ─────────────────────────────────────────────────────────────────

const SourceRegistryContext = createContext<SourceRegistryContextType | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const SourceRegistryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

  // Créer le registry une seule fois
  const { current: instance } = useRef(createSourceRegistry());
  const { registry, acquirers } = instance;

  const { setScreenShareSlots } = useMediaStream();

  // ── Compteur de version pour forcer les re-renders ────────────────────────
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion(v => v + 1), []);

  // Un vrai montage de ce provider (premier affichage, ou changement de canal) signifie
  // que le flux getDisplayMedia précédent — s'il existait — est de toute façon perdu
  // irrémédiablement (aucun moyen de le "reprendre"). Le nom que l'utilisateur avait donné
  // à l'emplacement ne décrit donc plus rien de réel : on le réinitialise. `config`
  // (fréquence d'images, résolution, curseur, audio) reste inchangée : ce sont des
  // préférences valides pour la prochaine capture, pas une description du flux perdu.
  useEffect(() => {
    setScreenShareSlots(prev => prev.map((slot, i) => ({
      ...slot,
      label: DEFAULT_SCREEN_SHARE_SLOTS[i]?.label ?? slot.label,
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsub = registry.subscribe((_event: SourceEvent) => {
      bump();
    });
    return () => {
      unsub();
      registry.destroy();
    };
  }, [registry, bump]);

  // ── Wrappers stables ──────────────────────────────────────────────────────

  const register = useCallback(
    (options: RegisterOptions) => registry.register(options),
    [registry],
  );

  const unregister = useCallback(
    (sourceId: string) => registry.unregister(sourceId),
    [registry],
  );

  const release = useCallback(
    (sourceId: string) => registry.release(sourceId),
    [registry],
  );

  const forceRelease = useCallback(
    (sourceId: string) => registry.forceRelease(sourceId),
    [registry],
  );

  const borrow = useCallback(
    (sourceId: string) => registry.borrow(sourceId),
    [registry],
  );

  const injectStream = useCallback(
    (sourceId: string, stream: MediaStream) => registry.injectStream(sourceId, stream),
    [registry],
  );

  const ejectStream = useCallback(
    (sourceId: string) => registry.ejectStream(sourceId),
    [registry],
  );

  /**
   * Ce que faisait `destroy()` au changement de chaîne — moins la coupure des
   * périphériques locaux.
   *
   * Tant que ce provider vivait sous la `key` par chaîne, son démontage détruisait le
   * registre entier, ce qui était juste pour les flux de la chaîne quittée (lecteurs
   * hls.js, flux distants, scènes) et faux pour la webcam et le partage d'écran : ces
   * deux-là sont locaux, coûteux à rétablir — un partage d'écran redemande même une
   * autorisation au navigateur — et n'ont rien à voir avec la chaîne affichée.
   *
   * Depuis que le registre est monté à la racine, plus rien ne libère les premiers : la
   * régie appelle donc ceci en se démontant. Sans ça, un lecteur HLS de la chaîne quittée
   * continuerait de tirer ses segments en arrière-plan, indéfiniment.
   */
  const releaseChannelScopedSources = useCallback(() => {
    registry.getAll()
      .filter(source => !PERSISTENT_SOURCE_TYPES.includes(source.config.type))
      .forEach(source => {
        try { registry.forceRelease(source.id); } catch {}
      });
  }, [registry]);

  // ── Lecture réactive ──────────────────────────────────────────────────────

  const sources = useMemo(
    () => registry.getAll(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registry, version],
  );

  const sourcesByType = useCallback(
    (type: SourceType) => registry.getAllByType(type),
    [registry],
  );

  const getSource = useCallback(
    (id: string) => registry.get(id),
    [registry],
  );

  const getStream = useCallback(
    (id: string) => registry.getStream(id),
    [registry],
  );

  const getState = useCallback(
    (id: string) => registry.getState(id),
    [registry],
  );

  // ── Valeur du contexte ────────────────────────────────────────────────────

  const value = useMemo<SourceRegistryContextType>(() => ({
    registry, acquirers,
    register, unregister,
    release, forceRelease,
    borrow, injectStream, ejectStream, releaseChannelScopedSources,
    sources, sourcesByType, getSource, getStream, getState,
  }), [
    registry, acquirers,
    register, unregister,
    release, forceRelease,
    borrow, injectStream, ejectStream, releaseChannelScopedSources,
    sources, sourcesByType, getSource, getStream, getState,
  ]);

  return (
    <SourceRegistryContext.Provider value={value}>
      {children}
    </SourceRegistryContext.Provider>
  );
};

// ─── Hook consommateur ────────────────────────────────────────────────────────

export function useSourceRegistry(): SourceRegistryContextType {
  const ctx = useContext(SourceRegistryContext);
  if (!ctx) {
    throw new Error('useSourceRegistry must be used within SourceRegistryProvider');
  }
  return ctx;
}

// ─── Hook utilitaire : suivre une source spécifique ───────────────────────────

export function useSource(sourceId: string): Source | undefined {
  const { getSource } = useSourceRegistry();
  return getSource(sourceId);
}

export function useSourceStream(sourceId: string): MediaStream | null {
  const { getStream } = useSourceRegistry();
  return getStream(sourceId);
}